import { THEME_INIT_SCRIPT, startThemeSync } from "@/lib/theme";
import { isContentPage } from "@/content-site/lib/paths";
import { ClerkProvider, useUser } from "@clerk/tanstack-react-start";
import {
  HeadContent,
  ScriptOnce,
  Scripts,
  createRootRouteWithContext,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import { AppRouteError } from "@/components/app-route-error";
import { bavimailClerkAppearance } from "@/lib/clerk-appearance";
import { NotFoundTerminal } from "@/components/motion/not-found/terminal";
import { captureFirstTouchAttribution, getFirstTouchEventProperties } from "@/lib/attribution";
import { initBrowserGlitchTipClient } from "@/lib/glitchtip-client-init";
import { resolveMaintenanceMode } from "@/features/maintenance/queries";
import { MAINTENANCE_LANDING_PATH, isMaintenanceExemptPath } from "@/lib/maintenance";
import { setContext, setTag } from "@/lib/glitchtip";
import { resetPostHog } from "@/lib/posthog-client";
import {
  BAVLIO_GA4_CONFIG_SCRIPT,
  BAVLIO_GA4_LINKER_SCRIPT,
  sendBavlioGa4PageView,
} from "@/lib/bavlio-ga4";
import type { AppRouterContext } from "@/lib/router-context";

import appCss from "../styles.css?url";

const DevelopmentDevtools = import.meta.env.DEV
  ? lazy(async () => {
      const [{ TanStackRouterDevtoolsPanel }, { TanStackDevtools }] = await Promise.all([
        import("@tanstack/react-router-devtools"),
        import("@tanstack/react-devtools"),
      ]);

      return {
        default: function DevelopmentDevtoolsPanel() {
          return (
            <TanStackDevtools
              config={{
                position: "bottom-right",
              }}
              plugins={[
                {
                  name: "Tanstack Router",
                  render: <TanStackRouterDevtoolsPanel />,
                },
              ]}
            />
          );
        },
      };
    })
  : null;

export const Route = createRootRouteWithContext<AppRouterContext>()({
  // The server entry already turns these requests away, but client-side
  // navigations never reach it. The flag comes from the server (seeded during
  // SSR, re-checked once stale), never from the bundle's build-time env, so an
  // open tab follows the server within MAINTENANCE_STATUS_STALE_TIME_MS.
  // Preloads still resolve it, which warms the check before the click, but
  // never redirect: they are speculative, and the real click lands here.
  beforeLoad: async ({ context, location, preload }) => {
    if (isContentPage(location.pathname)) return;
    const maintenanceMode = await resolveMaintenanceMode(context.queryClient);

    if (!maintenanceMode || preload || isMaintenanceExemptPath(location.pathname)) {
      return;
    }

    // `replace` so a blocked URL does not sit in history and bounce the user
    // again when they hit back.
    throw redirect({ to: MAINTENANCE_LANDING_PATH, replace: true });
  },

  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "Bavimail",
      },
      {
        name: "theme-color",
        content: "#0d0d0f",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        sizes: "any",
        href: "/favicon.svg?v=6259f4c16a",
      },
      {
        rel: "icon",
        type: "image/x-icon",
        href: "/favicon.ico?v=d1fed100ed",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png?v=7b23b6479e",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png?v=b897f1d47a",
      },
      {
        rel: "apple-touch-icon",
        href: "/apple-touch-icon.png?v=2eab9742bb",
      },
      {
        rel: "manifest",
        href: "/manifest.json?v=b93d84d2c1",
      },
    ],
  }),

  errorComponent: AppRouteError,
  notFoundComponent: NotFoundPage,
  shellComponent: RootDocument,
});

function NotFoundPage() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-[#0d0d0f] px-6">
      <NotFoundTerminal homeHref="/" homeLabel="Go home" browseHref="/login" browseLabel="Log in" />
    </main>
  );
}

function usePostHogClient() {
  const [client, setClient] = useState<typeof posthog | null>(null);

  useEffect(() => {
    const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
    const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
    if (!key || !host) return;

    posthog.init(key, {
      api_host: host,
      defaults: "2026-01-30",
      capture_pageview: true,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
      },
    });
    setClient(posthog);
  }, []);

  return client;
}

/**
 * Bavlio Inc.'s GA4 stream has no automatic page views, so each in-app
 * navigation to a public page sends one. The landing page's view comes from
 * the inline Google tag script.
 */
function BavlioGa4PageViews({ pathname, enabled }: { pathname: string; enabled: boolean }) {
  const isLanding = useRef(true);

  useEffect(() => {
    if (isLanding.current) {
      isLanding.current = false;
      return;
    }
    if (!enabled) return;
    // After the new route's head has set document.title.
    const timer = window.setTimeout(sendBavlioGa4PageView, 0);
    return () => window.clearTimeout(timer);
  }, [pathname, enabled]);

  return null;
}

function FirstTouchCapture() {
  useEffect(() => {
    captureFirstTouchAttribution();
  }, []);

  return null;
}

function PostHogIdentity({ client }: { client: typeof posthog }) {
  const { isLoaded, user } = useUser();
  const lastSeenUserId = useRef<string | null>(null);
  const hasEvaluatedAuthState = useRef(false);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    const firstTouch = captureFirstTouchAttribution();
    const isFreshLoad = !hasEvaluatedAuthState.current;
    let storedUserId: string | null = null;

    if (isFreshLoad) {
      try {
        storedUserId = window.sessionStorage.getItem("bm_last_uid");
        lastSeenUserId.current = storedUserId;
      } catch {
        // The in-memory tracker still covers identity changes during this mount.
      }
    }

    const currentUserId = user?.id ?? null;
    const previousUserId = lastSeenUserId.current;
    let didReset = false;

    if (currentUserId !== previousUserId) {
      if (previousUserId !== null) {
        resetPostHog();
        didReset = true;
      }

      if (currentUserId !== null && user) {
        const firstTouchProperties = getFirstTouchEventProperties(firstTouch);
        if (client.get_distinct_id() !== currentUserId) {
          const isIdentifiedAsOther =
            typeof client._isIdentified === "function" && client._isIdentified() === true;
          if (isIdentifiedAsOther && !didReset) {
            resetPostHog();
          }
          client.identify(currentUserId, {
            email: user.primaryEmailAddress?.emailAddress,
            clerk_created_at: user.createdAt?.toISOString(),
          });
          client.people.set_once(firstTouchProperties);
        }

        const loginStorageKey = `bm_login_evt_${currentUserId}`;
        let alreadyCaptured = false;
        try {
          alreadyCaptured = window.sessionStorage.getItem(loginStorageKey) === "1";
        } catch {
          // The identity-change guard still prevents duplicate captures during this mount.
        }
        if (!alreadyCaptured) {
          client.capture("login_completed");
          try {
            window.sessionStorage.setItem(loginStorageKey, "1");
          } catch {
            // The identity-change guard still prevents duplicate captures during this mount.
          }
        }

        const createdAt = user.createdAt?.getTime();
        const accountAge = createdAt === undefined ? undefined : Date.now() - createdAt;
        const isRecentSignup = accountAge !== undefined && accountAge >= 0 && accountAge <= 600_000;
        const analyticsMetadata = user.unsafeMetadata.analytics;
        const signupCapturedInMetadata =
          typeof analyticsMetadata === "object" &&
          analyticsMetadata !== null &&
          !Array.isArray(analyticsMetadata) &&
          "signup_captured" in analyticsMetadata &&
          analyticsMetadata.signup_captured === true;
        const signupStorageKey = `bm_signup_evt_${currentUserId}`;
        let signupAlreadyCaptured = false;

        try {
          signupAlreadyCaptured = window.localStorage.getItem(signupStorageKey) === "1";
        } catch {
          // The identity-change guard still prevents duplicate captures during this mount.
        }

        if (isRecentSignup && !signupCapturedInMetadata) {
          if (!signupAlreadyCaptured) {
            client.capture("signup_completed", firstTouchProperties);
            try {
              window.localStorage.setItem(signupStorageKey, "1");
            } catch {
              // The identity-change guard still prevents duplicate captures during this mount.
            }
          }

          void (async () => {
            try {
              await user.updateMetadata({
                unsafeMetadata: {
                  ...(firstTouch && !user.unsafeMetadata.first_touch
                    ? { first_touch: firstTouch }
                    : {}),
                  analytics: { signup_captured: true },
                },
              });
            } catch (error) {
              console.warn("Failed to persist signup attribution to Clerk.", error);
            }
          })();
        }
      }

      try {
        if (currentUserId === null) {
          window.sessionStorage.removeItem("bm_last_uid");
        } else {
          window.sessionStorage.setItem("bm_last_uid", currentUserId);
        }
      } catch {
        // The ref remains the source of truth for this mount when storage is unavailable.
      }

      lastSeenUserId.current = currentUserId;
    }

    if (isFreshLoad && currentUserId === null && !didReset) {
      const isIdentified =
        typeof client._isIdentified === "function"
          ? client._isIdentified() === true
          : storedUserId !== null && client.get_distinct_id() === storedUserId;

      if (isIdentified) {
        resetPostHog();
      }
    }

    hasEvaluatedAuthState.current = true;
  }, [client, isLoaded, user]);

  return null;
}

function RootDocument({ children }: { children: React.ReactNode }) {
  useEffect(startThemeSync, []);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const hideDevtools = pathname.startsWith("/dashboard");
  const isAppRoute = pathname.startsWith("/dashboard") || pathname.startsWith("/login");

  useEffect(() => {
    void initBrowserGlitchTipClient();
  }, []);

  useEffect(() => {
    void setTag({ key: "route.path", value: pathname });
    void setContext({
      name: "route",
      context: {
        pathname,
      },
    });
  }, [pathname]);

  const posthogClient = usePostHogClient();

  const content = (
    <ClerkProvider appearance={bavimailClerkAppearance}>
      <FirstTouchCapture />
      <BavlioGa4PageViews pathname={pathname} enabled={!isAppRoute} />
      {posthogClient ? <PostHogIdentity client={posthogClient} /> : null}
      {children}
      {import.meta.env.DEV && !hideDevtools && DevelopmentDevtools ? (
        <Suspense fallback={null}>
          <DevelopmentDevtools />
        </Suspense>
      ) : null}
    </ClerkProvider>
  );

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <ScriptOnce>{THEME_INIT_SCRIPT}</ScriptOnce>
        <HeadContent />
      </head>
      <body className="min-h-screen bg-background text-foreground antialiased">
        {!isAppRoute && (
          <>
            {/* Google Ads + GA4 (Bavimail's stream, then Bavlio Inc.'s, see lib/bavlio-ga4.ts) */}
            <script async src="https://www.googletagmanager.com/gtag/js?id=AW-18084757067" />
            <script
              dangerouslySetInnerHTML={{
                __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              ${BAVLIO_GA4_LINKER_SCRIPT}
              gtag('config', 'AW-18084757067');
              gtag('config', 'G-82D8H915MS');
              ${BAVLIO_GA4_CONFIG_SCRIPT}
            `,
              }}
            />
            {/* Meta Pixel */}
            <script
              dangerouslySetInnerHTML={{
                __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '783598257933995');
              fbq('track', 'PageView');
            `,
              }}
            />
            {/* Reddit Pixel */}
            <script
              dangerouslySetInnerHTML={{
                __html: `
              !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
              rdt('init','a2_iskavjljiehr');
              rdt('track', 'PageVisit');
            `,
              }}
            />
          </>
        )}
        {posthogClient ? (
          <PostHogProvider client={posthogClient}>{content}</PostHogProvider>
        ) : (
          content
        )}
        <Scripts />
      </body>
    </html>
  );
}
